import { captureException } from '@sentry/sveltekit';

// Track active retries per player to allow cleanup
const active_retries = new WeakMap();

const retry_mux_player_load = async (
    player,
    attempt = 0,
    original_error = null,
    abort_controller = null
) => {
    // Check if player is still in DOM
    if (!player.isConnected) {
        return;
    }

    // Create abort controller if not provided
    if (!abort_controller) {
        abort_controller = new AbortController();
        active_retries.set(player, abort_controller);
    }

    const playback_id = player.getAttribute('playback-id') || 'unknown';

    if (attempt >= 5) {
        const error =
            original_error ||
            new Error('Mux player failed to load after 5 attempts');
        captureException(error, {
            tags: {
                component: 'mux-player',
                playback_id
            },
            extra: {
                attempts: attempt,
                playback_id,
                original_error_name: original_error?.name,
                original_error_message: original_error?.message
            }
        });
        active_retries.delete(player);
        return;
    }

    // Wait with abort support
    try {
        await new Promise((resolve, reject) => {
            const timeout = setTimeout(
                resolve,
                Math.min(1000 * Math.pow(2, attempt), 30000)
            );
            abort_controller.signal.addEventListener('abort', () => {
                clearTimeout(timeout);
                reject(new Error('Retry aborted'));
            });
        });
    } catch {
        // Retry was aborted
        return;
    }

    // Check again if player is still in DOM
    if (!player.isConnected || abort_controller.signal.aborted) {
        active_retries.delete(player);
        return;
    }

    if (!playback_id || playback_id === 'unknown') {
        active_retries.delete(player);
        return;
    }

    player.removeAttribute('playback-id');

    try {
        await new Promise((resolve, reject) => {
            const timeout = setTimeout(resolve, 100);
            abort_controller.signal.addEventListener('abort', () => {
                clearTimeout(timeout);
                reject(new Error('Retry aborted'));
            });
        });
    } catch {
        // Retry was aborted
        active_retries.delete(player);
        return;
    }

    if (!player.isConnected || abort_controller.signal.aborted) {
        active_retries.delete(player);
        return;
    }

    player.setAttribute('playback-id', playback_id);

    // Success cleanup: if playback resumes, clear active retry state
    const on_success = () => {
        if (abort_controller && !abort_controller.signal.aborted) {
            // no-op; controller is only used for backoff sleeps
        }
        active_retries.delete(player);
    };
    player.addEventListener('playing', on_success, { once: true });
    player.addEventListener('canplay', on_success, { once: true });

    const error_handler = (event) => {
        if (abort_controller.signal.aborted) return;
        player.removeEventListener('error', error_handler);
        const retry_error = event.error || player.error || original_error;
        retry_mux_player_load(
            player,
            attempt + 1,
            original_error || retry_error,
            abort_controller
        );
    };

    player.addEventListener('error', error_handler, { once: true });
};

const setup_mux_player_error_handler = (player) => {
    if (player.dataset.errorHandlerAttached) return;
    player.dataset.errorHandlerAttached = 'true';

    player.addEventListener('error', (event) => {
        // Avoid overlapping retries: if a retry is already active for this
        // player, ignore further global error handling
        if (active_retries.has(player)) return;
        const error = event.error || player.error;
        const playback_id = player.getAttribute('playback-id') || 'unknown';

        const should_retry =
            error?.name === 'NotSupportedError' ||
            error?.name === 'NetworkError' ||
            error?.message?.includes('Failed to load') ||
            error?.message?.includes('network');

        if (should_retry) {
            retry_mux_player_load(player, 0, error);
        } else if (error) {
            captureException(error, {
                tags: {
                    component: 'mux-player',
                    playback_id
                },
                extra: {
                    playback_id,
                    error_name: error?.name,
                    error_message: error?.message
                }
            });
        }
    });
};

export function setup_global_mux_error_handling() {
    // Setup handlers for existing players
    document
        .querySelectorAll('mux-player')
        .forEach(setup_mux_player_error_handler);

    // Watch for new players added dynamically (debounced for performance)
    let pending_players = new Set();
    let timeout_id = null;

    const process_pending_players = () => {
        pending_players.forEach(setup_mux_player_error_handler);
        pending_players.clear();
        timeout_id = null;
    };

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Handle removed nodes - cancel any active retries
            mutation.removedNodes.forEach((node) => {
                if (node.nodeType === 1 && node instanceof Element) {
                    if (node.tagName === 'MUX-PLAYER') {
                        const abort_controller = active_retries.get(node);
                        if (abort_controller) {
                            abort_controller.abort();
                            active_retries.delete(node);
                        }
                    } else {
                        node.querySelectorAll('mux-player').forEach(
                            (player) => {
                                const abort_controller =
                                    active_retries.get(player);
                                if (abort_controller) {
                                    abort_controller.abort();
                                    active_retries.delete(player);
                                }
                            }
                        );
                    }
                }
            });

            // Handle added nodes - setup error handlers
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1 && node instanceof Element) {
                    if (node.tagName === 'MUX-PLAYER') {
                        pending_players.add(node);
                    } else {
                        node.querySelectorAll('mux-player').forEach(
                            (player) => {
                                pending_players.add(player);
                            }
                        );
                    }
                }
            });
        });

        if (pending_players.size > 0 && !timeout_id) {
            timeout_id = setTimeout(process_pending_players, 100);
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
        observer.disconnect();
        if (timeout_id) {
            clearTimeout(timeout_id);
        }
        // Cancel all active retries
        document.querySelectorAll('mux-player').forEach((player) => {
            const abort_controller = active_retries.get(player);
            if (abort_controller) {
                abort_controller.abort();
                active_retries.delete(player);
            }
        });
    };
}
